import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';

export const ServicesGrid = () => {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getCardStyle = (index) => {
    let visibleCount = 1;
    if (windowWidth >= 1024) visibleCount = 3;
    else if (windowWidth >= 768) visibleCount = 2;

    const relativePos = index - current;
    
    let bgPosition = '50% 50%';
    let bgSize = '200% 100%';

    if (visibleCount === 3) {
      bgSize = '300% 100%';
      if (relativePos <= 0) bgPosition = '0% 50%';
      else if (relativePos === 1) bgPosition = '50% 50%';
      else bgPosition = '100% 50%';
    } else if (visibleCount === 2) {
      bgSize = '200% 100%';
      if (relativePos <= 0) bgPosition = '0% 50%';
      else bgPosition = '100% 50%';
    }

    return {
      backgroundImage: 'var(--master-gradient)',
      backgroundSize: bgSize,
      backgroundPosition: bgPosition,
      transition: 'background-position 0.5s ease-out'
    };
  };

  useEffect(() => {
    if (!api) return;

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const services = [
    {
      image: `${process.env.PUBLIC_URL}/whole-house-water-filter.png`,
      title: 'Whole House Systems',
      anchor: 'whole-house-systems',
      description: 'Custom whole-home water treatment systems designed to improve water quality from every tap, shower, and appliance.',
      features: ['Custom system design', 'Filtration and softening options', 'Professionally installed'],
    },
    {
      image: `${process.env.PUBLIC_URL}/aquify-reverse-osmosis.png`,
      title: 'Reverse Osmosis Systems',
      anchor: 'reverse-osmosis',
      description: 'Advanced RO systems for the purest drinking water, removing a wide range of contaminants.',
      features: ['Under-sink systems', 'High-efficiency filtration', 'Tankless options available'],
    },
    {
      image: `${process.env.PUBLIC_URL}/well-water-treatment.png`,
      title: 'Well Water Treatment',
      anchor: 'well-water-treatment',
      description: 'Specialized solutions for well water issues including iron, sulfur, bacteria, and sediment.',
      features: ['Complete well water testing', 'Custom treatment plans', 'Ongoing maintenance options'],
    },
    {
      image: `${process.env.PUBLIC_URL}/commercial-water-treatment-system.png`,
      title: 'Commercial Filtration',
      anchor: 'commercial-filtration',
      description: 'Professional-grade filtration systems designed for restaurants, offices, medical facilities, manufacturers, and other commercial properties.',
      features: ['High-capacity systems', 'Code-compliant installation', 'Service agreements available'],
    },
    {
      image: `${process.env.PUBLIC_URL}/water-evaluation.png`,
      title: 'Drinking Water Systems',
      anchor: 'drinking-water-systems',
      description: 'Point-of-use drinking water systems designed to provide cleaner, better-tasting water directly at the tap.',
      features: ['Inline filtration options', 'Cleaner, better-tasting water', 'Compact point-of-use installation'],
    }
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Our Water Solutions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-3">
            From residential to commercial, we provide certified water treatment systems professionally designed and installed for your specific needs.
          </p>
          <p className="text-sm font-semibold uppercase tracking-widest" style={{ color: '#1571BA' }}>
            Design • Sales • Installation • Service
          </p>
        </div>

        <div className="relative px-6 md:px-12 lg:px-16">
          <Carousel
            setApi={setApi}
            opts={{
              align: "start",
              loop: false,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {services.map((service, index) => (
                <CarouselItem key={index} className="pl-4 basis-full md:basis-1/2 lg:basis-1/3">
                  <div className="p-1 h-full">
                    <Card
                      className="h-full flex flex-col border border-white/10 transition-all duration-300 transform shadow-md hover:shadow-xl hover:-translate-y-1"
                      style={getCardStyle(index)}
                    >
                      <CardHeader className="flex-none">
                        <div className="w-full h-48 rounded-lg overflow-hidden mb-4 bg-slate-50">
                          <img
                            src={service.image}
                            alt={service.title}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <CardTitle className="text-2xl text-white">{service.title}</CardTitle>
                        <CardDescription className="text-base mt-2 text-white/85">
                          {service.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="flex flex-col flex-grow justify-end">
                        <ul className="space-y-2 mb-6">
                          {service.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center text-white/90">
                              <span className="w-2 h-2 rounded-full mr-3 flex-shrink-0 bg-[#8EDAF4]"></span>
                              {feature}
                            </li>
                          ))}
                        </ul>
                        <Link
                          to={`/services#${service.anchor}`}
                          className="bg-white text-[#111827] border border-[#111827] hover:bg-gray-100 block w-full text-center font-semibold py-2.5 rounded-md mt-auto shadow-md focus:outline-none focus:ring-2 focus:ring-[#1571BA] focus:ring-offset-2 transition-colors duration-200"
                        >
                          Learn More
                        </Link>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>

            <div className="hidden md:block">
              <CarouselPrevious className="absolute -left-4 lg:-left-12 top-1/2 -translate-y-1/2 w-12 h-12 bg-white border-2 border-gray-200 text-[#0C4288] hover:bg-slate-50 transition-colors shadow-md" />
              <CarouselNext className="absolute -right-4 lg:-right-12 top-1/2 -translate-y-1/2 w-12 h-12 bg-white border-2 border-gray-200 text-[#0C4288] hover:bg-slate-50 transition-colors shadow-md" />
            </div>
          </Carousel>
        </div>

        {/* Carousel Dots */}
        <div className="flex justify-center gap-3 mt-8">
          {Array.from({ length: count }).map((_, i) => (
            <button
              key={i}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1571BA] ${i === current ? "bg-[#1571BA] scale-125" : "bg-slate-300 scale-100"
                }`}
              onClick={() => api?.scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;
